import React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { TYPOGRAPHY_SIZE } from 'mm-theme-configuration/dist/consts';
import { useTheme } from '../../../theming/useTheme';
import {
  isLastLink,
  SecondaryLinksComponentProps,
  SecondaryLinksProps, SecondaryLinksThemeProps,
} from '../../components/navigation/header/secondaryHeader/SecondaryLinks';
import { maxVisibleLinksOnLargeAndXLarge } from '../../components/navigation/header/secondaryHeader/SecondaryHeader';
import { VrLineHeight } from '../../../theming/verticalRhythm/verticalRhythm';
import { HeaderLinksDataProps } from '../../components/navigation/header/Header';
import { generateLinkStyleFromTheme, TextLinkComponent } from '../../partials/link/TextLink';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import { AMPMoreLinks } from './AMPMoreLinks';
import { Span, semanticCSS } from '../../../typography/semanticTags';
import { getStyleBySize, FontSizesStyles } from '../../../typography/semanticTags/semanticTags.utils';

const componentConfigThemeKey = 'secondaryLinks';

const getTextStyle = (vrLineHeight: VrLineHeight, isActiveLink: boolean, lastLink: boolean) => {
  return StyleSheet.create({
    link: {
      width: 'max-content',
      fontWeight: isActiveLink ? 600 : 400,
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        paddingRight: lastLink ? `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 2}px` : 0,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        paddingRight: lastLink ? `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 2}px` : 0,
      },
    },
  });
};

export const getContainerStyle = (vrLineHeight: VrLineHeight) => {
  return StyleSheet.create({
    container: {
      display: 'grid',
      gridAutoFlow: 'column',
      alignItems: 'center',
      height: '100%',
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
        overflowX: 'auto',
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
        overflowX: 'auto',
      },
    },
  });
};

const getCreateLinkFunction = (
  idleColor: string,
  activeStateColor: string,
  hoverStateColor: string,
  vrLineHeight: VrLineHeight,
  linksLength: number,
  pagePath: string,
  fontSizesStyles: FontSizesStyles,
) => {
  const createLink = (link: HeaderLinksDataProps, index: number) => {
    const { href, text } = link;
    const isActiveLink = href === pagePath;

    const linkStyleBasedOnScreen = linksLength > maxVisibleLinksOnLargeAndXLarge && index >= maxVisibleLinksOnLargeAndXLarge - 1
      ? {
        [MEDIA_BREAKPOINTS.LARGE]: {
          display: 'none',
        },
        [MEDIA_BREAKPOINTS.SMALL]: {
          display: 'inline-flex',
        },
        [MEDIA_BREAKPOINTS.MEDIUM]: {
          display: 'inline-flex',
        },
      }
      : {};
    const linkStyle = generateLinkStyleFromTheme({
      idleColor: isActiveLink ? activeStateColor : idleColor,
      activeStateColor,
      hoverStateColor: isActiveLink ? activeStateColor : hoverStateColor,
    }, linkStyleBasedOnScreen);
    const style = getTextStyle(vrLineHeight, isActiveLink, isLastLink(index, linksLength));
    const contentStyle = getStyleBySize(fontSizesStyles, isActiveLink ? TYPOGRAPHY_SIZE.BIGGER : TYPOGRAPHY_SIZE.NORMAL);
    return (
      <TextLinkComponent key={index} href={href} linkStyle={linkStyle}>
        <Span styles={semanticCSS(contentStyle, style.link)}>{text}</Span>
      </TextLinkComponent>
    );
  };
  return createLink;
};

export const AMPSecondaryLinksComponent: React.FunctionComponent<SecondaryLinksComponentProps> = props => {
  const { idleColor, moreLinksText, vrLineHeight, activeStateColor, hoverStateColor, links, pagePath, fontSizesStyles } = props;
  const moreLinks = links.length > maxVisibleLinksOnLargeAndXLarge ? links.slice(maxVisibleLinksOnLargeAndXLarge - 1) : [];
  const style = getContainerStyle(vrLineHeight);
  return (
    <React.Fragment>
      <div className={css(style.container)}>
        {links.map(getCreateLinkFunction(idleColor, activeStateColor, hoverStateColor, vrLineHeight, links.length, pagePath, fontSizesStyles))}
      </div>
      {moreLinks.length > 0 ? <AMPMoreLinks links={moreLinks} moreLinksText={moreLinksText} pagePath={pagePath} /> : null}
    </React.Fragment>
  );
};

const mapThemeProps = (theme: SecondaryLinksThemeProps) => {
  return {
    idleColor: theme.idleColor,
    activeStateColor: theme.activeStateColor,
    hoverStateColor: theme.hoverStateColor,
    vrLineHeight: theme.vrLineHeight,
    hoverOtherLinksColors: theme.hoverOtherLinksColors,
    fontSizesStyles: theme.fontSizesStyles,
  };
};

export const AMPSecondaryLinks = (props: SecondaryLinksProps) => {
  const themeContext = useTheme(mapThemeProps, componentConfigThemeKey);
  return <AMPSecondaryLinksComponent {...themeContext} {...props} />;
};
